import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Menu, X } from 'lucide-react';

export function LandingNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Al llegar a "/" con un hash (ej. desde /precios), hace scroll a la sección
  useEffect(() => {
    if (onLanding && location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 150);
    }
  }, [onLanding, location.hash]);

  const goToSection = (id) => {
    if (onLanding) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${id}`);
    }
  };

  const links = [
    { id: 'problema', label: 'El problema' },
    { id: 'solucion', label: 'La solución' },
    { id: 'precios', label: 'Precios', to: '/precios' },
    { id: 'faq', label: 'Preguntas' },
  ];

  return (
    <header
      data-testid="landing-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || !onLanding ? 'backdrop-blur-xl bg-black/60 border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2 text-white font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Sparkles size={20} className="text-[#00E5FF]" />
          Sera
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-300">
          {links.map((l) => (
            l.to ? (
              <Link key={l.id} to={l.to} data-testid={`landing-nav-link-${l.id}`} className="hover:text-white transition-colors">
                {l.label}
              </Link>
            ) : (
              <button
                key={l.id}
                data-testid={`landing-nav-link-${l.id}`}
                onClick={() => goToSection(l.id)}
                className="hover:text-white transition-colors"
              >
                {l.label}
              </button>
            )
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <Link to="/sign-in" data-testid="landing-nav-signin-link" className="text-sm text-zinc-400 hover:text-white transition-colors">
            Iniciar sesión
          </Link>
          <button
            data-testid="landing-nav-demo-cta"
            onClick={() => goToSection('demo')}
            className="rounded-full bg-[#00E5FF] text-black text-sm font-semibold px-5 py-2.5 sera-glow-cta"
          >
            Solicita una demo
          </button>
        </div>

        <button
          data-testid="landing-nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="landing-nav-mobile-menu" className="md:hidden bg-black/90 border-t border-white/10 px-6 py-5 flex flex-col gap-4">
          {links.map((l) => (
            l.to ? (
              <Link key={l.id} to={l.to} onClick={() => setOpen(false)} className="text-left text-zinc-300 text-sm">{l.label}</Link>
            ) : (
              <button
                key={l.id}
                onClick={() => { goToSection(l.id); setOpen(false); }}
                className="text-left text-zinc-300 text-sm"
              >
                {l.label}
              </button>
            )
          ))}
          <Link to="/sign-in" className="text-left text-zinc-400 text-sm">Iniciar sesión</Link>
          <button
            onClick={() => { goToSection('demo'); setOpen(false); }}
            className="rounded-full bg-[#00E5FF] text-black text-sm font-semibold px-5 py-2.5 w-full"
          >
            Solicita una demo
          </button>
        </div>
      )}
    </header>
  );
}
